class TrCartGiftOptions extends HTMLElement {
	selectors = {
		cartDrawer: '[data-tr-js-cart-gift-options-cart-drawer]',
		overlay: '[data-tr-js-cart-gift-options-overlay]',
		mainDrawer: '[data-tr-js-cart-gift-options-main-drawer]',
		openButton: '[data-tr-js-cart-gift-options-open]',
		closeButton: '[data-tr-js-cart-gift-options-close]',
		product: '[data-tr-js-cart-gift-options-product]',
		checkbox: '[data-tr-js-cart-gift-options-checkbox]',
		quantityInput: '[data-tr-js-cart-gift-options-quantity-input]',
		quantityButton: '[data-tr-js-cart-gift-options-quantity-button]',
		messageInput: '[data-tr-js-cart-gift-options-message]',
		resetButton: '[data-tr-js-cart-gift-options-reset]',
		applyButton: '[data-tr-js-cart-gift-options-apply]',
		error: '[data-tr-js-cart-gift-options-error]',
		currentItems: '[data-tr-js-cart-gift-options-current-items]'
	};

	attributes = {
		ariaHidden: 'aria-hidden',
		disabled: 'disabled',
		hidden: 'hidden',
		inert: 'inert'
	};

	stateClasses = {
		isActive: 'is-active',
		isGiftOptionsOpen: 'is-gift-options-open',
		isLoading: 'is-loading'
	};

	transitionFallbackDuration = 550;

	connectedCallback() {
		this.cartDrawerElement = this.closest('cart-drawer');
		this.cartDrawerContentElement = this.cartDrawerElement.querySelector(this.selectors.cartDrawer);
		this.overlayElement = this.cartDrawerElement.querySelector(this.selectors.overlay);
		this.mainDrawerElement = this.cartDrawerElement.querySelector(this.selectors.mainDrawer);
		this.openButtonElements = this.cartDrawerElement.querySelectorAll(this.selectors.openButton);
		this.closeButtonElement = this.querySelector(this.selectors.closeButton);
		this.productElements = this.querySelectorAll(this.selectors.product);
		this.checkboxElements = this.querySelectorAll(this.selectors.checkbox);
		this.quantityInputElements = this.querySelectorAll(this.selectors.quantityInput);
		this.quantityButtonElements = this.querySelectorAll(this.selectors.quantityButton);
		this.messageInputElement = this.querySelector(this.selectors.messageInput);
		this.resetButtonElement = this.querySelector(this.selectors.resetButton);
		this.applyButtonElement = this.querySelector(this.selectors.applyButton);
		this.errorElement = this.querySelector(this.selectors.error);
		this.currentItemsElement = this.querySelector(this.selectors.currentItems);
		this.productElementByVariantId = new Map();
		this.checkboxElementByVariantId = new Map();
		this.quantityInputElementByVariantId = new Map();
		this.currentGiftItems = [];

		this.init();
		this.bindEvents();

		document.addEventListener('shopify:section:load', this.onSectionLoad);
		document.addEventListener('shopify:block:select', this.onBlockSelect);
	}

	disconnectedCallback() {
		this.openButtonElements.forEach(buttonElement => {
			buttonElement.removeEventListener('click', this.onOpenButtonClick);
		});
		this.overlayElement.removeEventListener('click', this.onOverlayClick);
		this.closeButtonElement.removeEventListener('click', this.onCloseButtonClick);
		this.resetButtonElement.removeEventListener('click', this.onResetButtonClick);
		this.applyButtonElement.removeEventListener('click', this.onApplyButtonClick);
		this.checkboxElements.forEach(checkboxElement => {
			checkboxElement.removeEventListener('change', this.onCheckboxChange);
		});
		this.quantityButtonElements.forEach(buttonElement => {
			buttonElement.removeEventListener('click', this.onQuantityButtonClick);
		});
		this.quantityInputElements.forEach(inputElement => {
			inputElement.removeEventListener('change', this.onQuantityInputChange);
		});
		this.removeEventListener('keyup', this.onKeyUp);
		document.removeEventListener('shopify:section:load', this.onSectionLoad);
		document.removeEventListener('shopify:block:select', this.onBlockSelect);

		this.destroy();
	}

	init() {
		this.currentGiftItems = this.getCurrentGiftItems();
		this.cacheProductMaps();
		this.updateProductStates();
		this.setGiftControlsDisabled(true);
		this.clearError();
	}

	bindEvents() {
		this.openButtonElements.forEach(buttonElement => {
			buttonElement.addEventListener('click', this.onOpenButtonClick);
		});
		this.overlayElement.addEventListener('click', this.onOverlayClick);
		this.closeButtonElement.addEventListener('click', this.onCloseButtonClick);
		this.resetButtonElement.addEventListener('click', this.onResetButtonClick);
		this.applyButtonElement.addEventListener('click', this.onApplyButtonClick);
		this.checkboxElements.forEach(checkboxElement => {
			checkboxElement.addEventListener('change', this.onCheckboxChange);
		});
		this.quantityButtonElements.forEach(buttonElement => {
			buttonElement.addEventListener('click', this.onQuantityButtonClick);
		});
		this.quantityInputElements.forEach(inputElement => {
			inputElement.addEventListener('change', this.onQuantityInputChange);
		});
		this.addEventListener('keyup', this.onKeyUp);
	}

	onSectionLoad = event => {
		if (!event.target.contains(this)) return;

		this.init();
	};

	onBlockSelect = () => {};

	onOpenButtonClick = event => {
		event.preventDefault();

		this.openGiftDrawer();
	};

	onOverlayClick = () => {
		this.resetDrawerState();
	};

	onCloseButtonClick = event => {
		event.preventDefault();

		this.closeGiftDrawer();
	};

	onResetButtonClick = event => {
		event.preventDefault();

		this.resetGiftOptions();
	};

	onApplyButtonClick = event => {
		event.preventDefault();

		this.applyGiftOptions();
	};

	onCheckboxChange = event => {
		const variantId = event.currentTarget.dataset.variantId;
		const quantity = event.currentTarget.checked ? this.getVariantQuantity(variantId, 1) : 0;

		this.setVariantQuantity(variantId, quantity);
	};

	onQuantityButtonClick = event => {
		event.preventDefault();

		const variantId = event.currentTarget.dataset.variantId;
		const currentQuantity = this.getVariantQuantity(variantId, 0);
		const nextQuantity =
			event.currentTarget.dataset.action === 'plus'
				? currentQuantity + 1
				: Math.max(currentQuantity - 1, 0);

		this.setVariantQuantity(variantId, nextQuantity);
	};

	onQuantityInputChange = event => {
		const variantId = event.currentTarget.dataset.variantId;
		const quantity = Math.max(parseInt(event.currentTarget.value, 10) || 0, 0);

		this.setVariantQuantity(variantId, quantity);
	};

	onKeyUp = event => {
		if (event.code.toUpperCase() !== 'ESCAPE') return;

		event.preventDefault();
		event.stopPropagation();

		this.closeGiftDrawer();
	};

	openGiftDrawer() {
		this.clearError();
		this.cartDrawerElement.classList.add(this.stateClasses.isGiftOptionsOpen);
		this.mainDrawerElement.setAttribute(this.attributes.ariaHidden, 'true');
		this.mainDrawerElement.setAttribute(this.attributes.inert, '');
		this.setAttribute(this.attributes.ariaHidden, 'false');
		this.setGiftControlsDisabled(false);
		removeTrapFocus();
		trapFocus(this, this.closeButtonElement);
	}

	closeGiftDrawer() {
		this.resetDrawerState();
		removeTrapFocus();
		trapFocus(this.cartDrawerContentElement, this.openButtonElements[0]);
	}

	resetDrawerState() {
		this.cartDrawerElement.classList.remove(this.stateClasses.isGiftOptionsOpen);
		this.setAttribute(this.attributes.ariaHidden, 'true');
		this.mainDrawerElement.setAttribute(this.attributes.ariaHidden, 'false');
		this.mainDrawerElement.removeAttribute(this.attributes.inert);
		this.setGiftControlsDisabled(true);
	}

	async resetGiftOptions() {
		this.setLoading(true);
		this.clearError();

		try {
			const parsedState = await this.sendCartUpdate(
				{
					updates: this.getGiftRemoveUpdates(),
					attributes: {
						[this.dataset.messageAttributeName]: ''
					}
				},
				true
			);

			this.renderCartDrawer(parsedState);
		} catch (error) {
			this.showError();
			this.setLoading(false);
		}
	}

	async applyGiftOptions() {
		const selectedItems = this.getSelectedItems();
		const message = this.messageInputElement.value.trim();

		this.setLoading(true);
		this.clearError();

		try {
			let parsedState = await this.sendCartUpdate(
				{
					updates: this.getGiftRemoveUpdates(),
					attributes: {
						[this.dataset.messageAttributeName]: message
					}
				},
				selectedItems.length === 0
			);

			if (selectedItems.length > 0) {
				parsedState = await this.sendCartAdd(selectedItems);
			}

			this.renderCartDrawer(parsedState);
		} catch (error) {
			this.showError();
			this.setLoading(false);
		}
	}

	getSelectedItems() {
		const selectedItems = [];

		this.quantityInputElements.forEach(inputElement => {
			const quantity = Math.max(parseInt(inputElement.value, 10) || 0, 0);

			if (quantity === 0) return;

			selectedItems.push({
				id: Number(inputElement.dataset.variantId),
				quantity,
				properties: {
					[this.dataset.giftPropertyName]: 'true'
				}
			});
		});

		return selectedItems;
	}

	getGiftRemoveUpdates() {
		const updates = {};

		this.currentGiftItems.forEach(item => {
			updates[item.key] = 0;
		});

		return updates;
	}

	getCurrentGiftItems() {
		if (!this.currentItemsElement.textContent.trim()) return [];

		try {
			return JSON.parse(this.currentItemsElement.textContent);
		} catch (error) {
			return [];
		}
	}

	getVariantQuantity(variantId, fallbackQuantity) {
		const inputElement = this.quantityInputElementByVariantId.get(variantId);

		if (!inputElement) return fallbackQuantity;

		return Math.max(parseInt(inputElement.value, 10) || fallbackQuantity, 0);
	}

	setVariantQuantity(variantId, quantity) {
		const inputElement = this.quantityInputElementByVariantId.get(variantId);
		const checkboxElement = this.checkboxElementByVariantId.get(variantId);
		const productElement = this.productElementByVariantId.get(variantId);

		if (!inputElement || !checkboxElement || !productElement) return;

		inputElement.value = quantity;
		checkboxElement.checked = quantity > 0;
		productElement.classList.toggle(this.stateClasses.isActive, quantity > 0);
	}

	cacheProductMaps() {
		this.productElementByVariantId.clear();
		this.checkboxElementByVariantId.clear();
		this.quantityInputElementByVariantId.clear();

		this.productElements.forEach(productElement => {
			this.productElementByVariantId.set(productElement.dataset.variantId, productElement);
		});
		this.checkboxElements.forEach(checkboxElement => {
			this.checkboxElementByVariantId.set(checkboxElement.dataset.variantId, checkboxElement);
		});
		this.quantityInputElements.forEach(inputElement => {
			this.quantityInputElementByVariantId.set(inputElement.dataset.variantId, inputElement);
		});
	}

	updateProductStates() {
		this.quantityInputElements.forEach(inputElement => {
			this.setVariantQuantity(inputElement.dataset.variantId, this.getVariantQuantity(inputElement.dataset.variantId, 0));
		});
	}

	async sendCartUpdate(body, shouldRenderSections) {
		const requestBody = { ...body };

		if (shouldRenderSections) {
			requestBody.sections = this.getSectionsToRender();
			requestBody.sections_url = window.location.pathname;
		}

		const response = await fetch(routes.cart_update_url, {
			...fetchConfig(),
			body: JSON.stringify(requestBody)
		});
		const parsedState = await response.json();

		if (!response.ok || parsedState.errors) throw new Error(this.dataset.errorMessage);

		return parsedState;
	}

	async sendCartAdd(items) {
		const response = await fetch(routes.cart_add_url, {
			...fetchConfig(),
			body: JSON.stringify({
				items,
				sections: this.getSectionsToRender(),
				sections_url: window.location.pathname
			})
		});
		const parsedState = await response.json();

		if (!response.ok || parsedState.status) throw new Error(this.dataset.errorMessage);

		return parsedState;
	}

	getSectionsToRender() {
		return ['cart-drawer', 'cart-icon-bubble'];
	}

	renderCartDrawer(parsedState) {
		this.closeGiftDrawer();

		if (this.cartDrawerElement && typeof this.cartDrawerElement.renderContents === 'function') {
			this.waitForGiftDrawerClose().then(() => {
				this.cartDrawerElement.renderContents(parsedState);
			});
			return;
		}

		window.location.reload();
	}

	waitForGiftDrawerClose() {
		return new Promise(resolve => {
			const complete = () => {
				window.clearTimeout(fallbackTimer);
				this.removeEventListener('transitionend', onTransitionEnd);
				resolve();
			};

			const onTransitionEnd = event => {
				if (event.target !== this || event.propertyName !== 'transform') return;

				complete();
			};
			const fallbackTimer = window.setTimeout(complete, this.transitionFallbackDuration);

			this.addEventListener('transitionend', onTransitionEnd);
		});
	}

	setLoading(isLoading) {
		this.classList.toggle(this.stateClasses.isLoading, isLoading);
		this.applyButtonElement.toggleAttribute(this.attributes.disabled, isLoading);
		this.resetButtonElement.toggleAttribute(this.attributes.disabled, isLoading);
		this.closeButtonElement.toggleAttribute(this.attributes.disabled, isLoading);
		this.checkboxElements.forEach(checkboxElement => {
			checkboxElement.toggleAttribute(this.attributes.disabled, isLoading);
		});
		this.quantityInputElements.forEach(inputElement => {
			inputElement.toggleAttribute(this.attributes.disabled, isLoading);
		});
		this.quantityButtonElements.forEach(buttonElement => {
			buttonElement.toggleAttribute(this.attributes.disabled, isLoading);
		});
		this.messageInputElement.toggleAttribute(this.attributes.disabled, isLoading);
	}

	setGiftControlsDisabled(isDisabled) {
		this.closeButtonElement.toggleAttribute(this.attributes.disabled, isDisabled);
		this.resetButtonElement.toggleAttribute(this.attributes.disabled, isDisabled);
		this.applyButtonElement.toggleAttribute(this.attributes.disabled, isDisabled);
		this.checkboxElements.forEach(checkboxElement => {
			checkboxElement.toggleAttribute(this.attributes.disabled, isDisabled);
		});
		this.quantityInputElements.forEach(inputElement => {
			inputElement.toggleAttribute(this.attributes.disabled, isDisabled);
		});
		this.quantityButtonElements.forEach(buttonElement => {
			buttonElement.toggleAttribute(this.attributes.disabled, isDisabled);
		});
		this.messageInputElement.toggleAttribute(this.attributes.disabled, isDisabled);
	}

	showError() {
		this.errorElement.textContent = this.dataset.errorMessage;
		this.errorElement.removeAttribute(this.attributes.hidden);
	}

	clearError() {
		this.errorElement.textContent = '';
		this.errorElement.setAttribute(this.attributes.hidden, '');
	}

	destroy() {
		this.productElementByVariantId.clear();
		this.checkboxElementByVariantId.clear();
		this.quantityInputElementByVariantId.clear();
	}
}

if (!customElements.get('tr-cart-gift-options')) {
	customElements.define('tr-cart-gift-options', TrCartGiftOptions);
}
